/**
 * НОВАЯ СБОРКА НА СЕРВЕРЕ → СЛЕДУЮЩИЙ ПЕРЕХОД ДЕЛАЕМ ПОЛНОЙ ЗАГРУЗКОЙ.
 *
 * ЧТО ЛЕЧИМ. `deployRecovery` реагирует ПОСЛЕ ошибки: чанк не доехал →
 * перезагрузка. Пользователь при этом успевает увидеть мигание, а в худшем
 * случае ErrorBoundary. Здесь — заранее: вкладка периодически узнаёт номер
 * сборки на сервере (`/__build.json`, его пишет vite.start.config.mts) и,
 * если он не совпадает со своим (`__SSR_BUILD_TIME__`), ближайший переход
 * на ДРУГОЙ адрес выполняет полной загрузкой страницы вместо SPA-перехода.
 * Для пользователя это просто чуть более долгий переход — без ошибок.
 *
 * КОГДА ПРОВЕРЯЕМ. На SPA-переходах (не чаще CHECK_INTERVAL_MS), при
 * возвращении на вкладку (visibilitychange) и при восстановлении страницы
 * из bfcache кнопкой «Назад» (pageshow persisted) — это ровно те моменты,
 * когда вкладка могла пропустить деплой.
 *
 * ОФОРМЛЕНИЕ ЗАКАЗА НЕ ТРОГАЕМ (решение Sergei, 06.10.2026). Ни с /cart/*,
 * ни на /cart/* (корзина, /cart/order/*, /cart/success) полная загрузка
 * не делается: человек посреди оформления, введённые данные и состояние
 * форм потерять нельзя. Чанки старой сборки там по-прежнему находятся в
 * архиве (`SSR_ASSET_ARCHIVE`, 14 дней), а сломанный случай ловит
 * `deployRecovery`. Как только пользователь уйдёт из оформления на любую
 * другую страницу — переход будет полным.
 *
 * ЧТО НЕ ЯВЛЯЕТСЯ ПЕРЕХОДОМ. Смена только search/hash (фильтры каталога,
 * сортировка, якоря) — без полной загрузки: потеря скролла и состояния
 * фильтров хуже, чем старая сборка ещё на один клик.
 *
 * ЗАЩИТА ОТ ЦИКЛА. Если после полной загрузки сборка всё ещё «не та»
 * (кэширующий прокси держит старый HTML, раскатка на несколько реплик),
 * переходы превратились бы в бесконечные полные загрузки. Поэтому отметка
 * в sessionStorage: не чаще раза в HARD_NAV_COOLDOWN_MS, дальше — молча
 * обычные SPA-переходы (страховкой остаётся deployRecovery).
 *
 * В dev и в SPA-сборке (vite.config.mts) `__SSR_BUILD_TIME__` или
 * `/__build.json` нет — модуль ничего не делает.
 */
import type { AnyRouter } from '@tanstack/react-router';

/** Адрес файла с номером сборки (кладётся в корень клиентской сборки). */
export const BUILD_META_PATH = '/__build.json';

/** Как часто спрашивать сервер на SPA-переходах. */
const CHECK_INTERVAL_MS = 5 * 60 * 1000;
/** Не чаще одной «версионной» полной загрузки за этот срок. */
const HARD_NAV_COOLDOWN_MS = 10 * 60 * 1000;
const HARD_NAV_MARK_KEY = 'buildVersionNavAt';

export const BUILD_VERSION_LOG =
  '[deploy] новая сборка на сервере — переход полной загрузкой';

/** Страницы оформления заказа: полной загрузки с них и на них не делаем. */
export const isCheckoutPath = (pathname: string): boolean =>
  /^\/cart(\/|$)/.test(pathname);

const ownBuildTime = (): number | null =>
  typeof __SSR_BUILD_TIME__ === 'number' ? __SSR_BUILD_TIME__ : null;

let serverBuildTime: number | null = null;
let lastCheckAt = 0;
let checking: Promise<void> | null = null;

/** Сборка на сервере отличается от нашей (в т.ч. откат на более старую). */
export const isStaleBuild = (
  own: number | null = ownBuildTime(),
  server: number | null = serverBuildTime
): boolean => own !== null && server !== null && server !== own;

export const checkServerBuild = (force = false): Promise<void> => {
  if (typeof window === 'undefined' || ownBuildTime() === null) {
    return Promise.resolve();
  }
  if (checking) {
    return checking;
  }
  if (!force && Date.now() - lastCheckAt < CHECK_INTERVAL_MS) {
    return Promise.resolve();
  }
  lastCheckAt = Date.now();
  checking = fetch(BUILD_META_PATH, { cache: 'no-store' })
    .then(res => (res.ok ? res.json() : null))
    .then((meta: { buildTime?: unknown } | null) => {
      if (typeof meta?.buildTime === 'number') {
        serverBuildTime = meta.buildTime;
      }
    })
    .catch(() => {
      // Нет сети или файла (dev) — просто не знаем; ничего не делаем
    })
    .finally(() => {
      checking = null;
    });
  return checking;
};

const hardNavAllowed = (): boolean => {
  try {
    const last = Number(window.sessionStorage.getItem(HARD_NAV_MARK_KEY)) || 0;
    if (Date.now() - last < HARD_NAV_COOLDOWN_MS) {
      return false;
    }
    window.sessionStorage.setItem(HARD_NAV_MARK_KEY, String(Date.now()));
    return true;
  } catch {
    // Без sessionStorage ограничитель не на чем держать — не рискуем
    return false;
  }
};

/** Решение по одному переходу; вынесено отдельно ради проверок. */
export const shouldHardNavigate = (
  fromPath: string | undefined,
  toPath: string,
  stale: boolean
): boolean =>
  stale &&
  fromPath !== undefined &&
  fromPath !== toPath &&
  !isCheckoutPath(fromPath) &&
  !isCheckoutPath(toPath);

/**
 * Полная загрузка адреса, на который роутер УЖЕ переходит.
 *
 * Вызывать только из подписчика роутера/истории (onBeforeNavigate,
 * history.subscribe). Тонкость @tanstack/history: push/replace меняют
 * location роутера и уведомляют подписчиков СИНХРОННО, а в адресную строку
 * (`history.pushState`) пишут позже — микрозадачей. То есть в момент
 * уведомления `window.location` ещё указывает на СТАРУЮ страницу, и голый
 * `location.reload()` перезагружал её же: пользователь кликал на Б, а после
 * перезагрузки снова оказывался на А (баг «старой вкладки», 05.11.2026).
 *
 * `history.flush()` синхронно дописывает отложенный pushState/replaceState
 * — после него адрес в браузере новый, и перезагрузка ведёт туда, куда
 * шёл пользователь, без лишней записи в истории. Для «Назад/Вперёд»
 * (popstate) отложенного нет, flush ничего не делает — адрес уже новый.
 * Страховка: если адрес всё же не тот — идём на новый явно.
 */
export const reloadIntoNavigation = (
  router: AnyRouter,
  toLocation: { pathname: string; href: string }
): void => {
  try {
    router.history.flush();
  } catch (e) {
    console.log('[hard-nav] history.flush не удался:', e);
  }
  if (window.location.pathname === toLocation.pathname) {
    window.location.reload();
  } else {
    window.location.assign(toLocation.href);
  }
};

/** Сколько вкладка может жить без перезагрузки (было в __root.tsx). */
export const TAB_MAX_AGE_MS = 4 * 60 * 60 * 1000;

export const TAB_AGE_LOG =
  '[deploy] вкладка открыта слишком давно — переход полной загрузкой';

let installed = false;

/**
 * Подписка на роутер. Зовётся один раз после гидрации (RootEffects).
 * @returns функция отписки
 */
export const installBuildVersionWatch = (router: AnyRouter): (() => void) => {
  if (typeof window === 'undefined' || installed || ownBuildTime() === null) {
    return () => {};
  }
  installed = true;

  const unsubscribe = router.subscribe('onBeforeNavigate', event => {
    const fromPath = event.fromLocation?.pathname;
    const toPath = event.toLocation.pathname;

    if (
      shouldHardNavigate(fromPath, toPath, isStaleBuild()) &&
      hardNavAllowed()
    ) {
      console.log(`${BUILD_VERSION_LOG}: ${fromPath} → ${toPath}`);
      reloadIntoNavigation(router, event.toLocation);
      return;
    }

    // Проверку запускаем на каждом переходе (с троттлингом) — результат
    // пригодится на СЛЕДУЮЩЕМ
    void checkServerBuild();
  });

  const onVisible = (): void => {
    if (document.visibilityState === 'visible') {
      void checkServerBuild(true);
    }
  };
  const onPageShow = (event: PageTransitionEvent): void => {
    if (event.persisted) {
      void checkServerBuild(true);
    }
  };
  document.addEventListener('visibilitychange', onVisible);
  window.addEventListener('pageshow', onPageShow);

  // первая проверка — не сразу, чтобы не конкурировать с загрузкой страницы
  const timer = window.setTimeout(() => void checkServerBuild(true), 30_000);

  return () => {
    unsubscribe();
    document.removeEventListener('visibilitychange', onVisible);
    window.removeEventListener('pageshow', onPageShow);
    window.clearTimeout(timer);
    installed = false;
  };
};

/**
 * СТАРАЯ ВКЛАДКА → СЛЕДУЮЩИЙ ПЕРЕХОД ПОЛНОЙ ЗАГРУЗКОЙ.
 *
 * Перенесено из checkURI (__root.tsx), где на любое изменение URL после
 * 2 часов жизни вкладки звался `location.reload()`. Две проблемы старого
 * варианта:
 *  1. reload срабатывал ДО того, как новый адрес попадал в адресную строку
 *     (см. reloadIntoNavigation) — перезагружалась страница, С КОТОРОЙ
 *     уходили, и переход терялся;
 *  2. не было исключения для оформления: перезагрузка посреди /cart/*
 *     теряла введённые данные.
 * Теперь правила те же, что у перехода по новой сборке (shouldHardNavigate):
 * только смена pathname (фильтры/сортировка/якоря — без перезагрузки) и
 * ни с /cart/*, ни на /cart/*. Работает и там, где номера сборки нет
 * (dev, SPA-сборка), — как и прежний checkURI. После перезагрузки отсчёт
 * начинается заново, поэтому цикла не бывает.
 *
 * @returns функция отписки
 */
export const installTabAgeWatch = (
  router: AnyRouter,
  maxAgeMs: number = TAB_MAX_AGE_MS
): (() => void) => {
  if (typeof window === 'undefined') {
    return () => {};
  }
  const startedAt = Date.now();

  return router.subscribe('onBeforeNavigate', event => {
    const fromPath = event.fromLocation?.pathname;
    const toPath = event.toLocation.pathname;
    const tooOld = Date.now() - startedAt > maxAgeMs;

    if (shouldHardNavigate(fromPath, toPath, tooOld)) {
      console.log(`${TAB_AGE_LOG}: ${fromPath} → ${toPath}`);
      reloadIntoNavigation(router, event.toLocation);
    }
  });
};
